import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { NO_DRAG_CLASS } from '../constants';

// The class the row's :hover rule reveals (see CompositeCard)
export const ROW_RESIZE_HANDLE_CLASS = 'studio-row-resize-handle';

// Centred on the row's bottom edge: a pill like a block's bottom resize
// handle on the canvas, over its children's own edges there
const Handle = styled.div`
  position: absolute;
  bottom: -6px;
  left: 50%;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 12px;
  transform: translateX(-50%);
  cursor: ns-resize;

  &::after {
    content: '';
    width: 24px;
    height: 6px;
    border-radius: 3px;
    border: 1.5px solid var(--color-studio-selection);
    background: var(--color-bg-raised);
    box-sizing: border-box;
  }
`;

// Drags the row's height from its bottom edge: onMove with how far it's
// been dragged (px) as it moves, then onEnd on release. Not a press on the
// row or block, so neither is selected or dragged. Double-clicked, the row
// sizes automatically again.
const RowResizeHandle = ({
  rowId,
  onStart,
  onMove,
  onEnd,
  onReset,
}: {
  rowId: string;
  onStart: () => void;
  onMove: (deltaPx: number) => void;
  onEnd: (deltaPx: number) => void;
  onReset: () => void;
}) => {
  const endDragRef = useRef<(() => void) | undefined>();
  useEffect(() => () => endDragRef.current && endDragRef.current(), []);

  return (
    <Handle
      className={`${NO_DRAG_CLASS} ${ROW_RESIZE_HANDLE_CLASS}`}
      title="Drag to resize the row"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.button !== 0) {
          return;
        }
        const startY = e.clientY;
        let deltaPx = 0;
        onStart();
        const move = (event: MouseEvent) => {
          deltaPx = event.clientY - startY;
          onMove(deltaPx);
        };
        const endDrag = () => {
          window.removeEventListener('mousemove', move);
          window.removeEventListener('mouseup', up);
          endDragRef.current = undefined;
        };
        const up = () => {
          endDrag();
          onEnd(deltaPx);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        endDragRef.current = endDrag;
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      data-testid={`studio-composite-row-resize-${rowId}`}
    />
  );
};

export default RowResizeHandle;
